<template>
  <VContainer id="product-details" class="text-center pa-0 mt-2">
    <VSheet>
      <div
        v-if="!product.product_id"
        class="d-flex border-b py-4 position-relative justify-center"
      >
        <LoadingOverlay width="75" height="75" class="position-relative" />
      </div>
      <VSheet
        v-if="product.product_id"
        ref="navbar"
        elevation="0"
        class="navbar navbar-expand-lg d-none d-lg-inline-block position-sticky bg-white w-100"
        :style="{ top: '150px', zIndex: 2 }"
      >
        <div
          id="nav-wrapper"
          class="c-clay font-weight-bold l-spacing-1 text-left"
        >
          <a
            v-if="hasDescriptions"
            class="smooth-scroll link-gray"
            :class="{ active: isActive('intro-description') }"
            href="#intro-description"
            @click.prevent="scroll('#intro-description')"
          >
            {{ translate('pdp_subnav_about_the_product') }}
          </a>
          <a
            v-if="isInBundle && !calculated.isBundle"
            class="smooth-scroll link-gray"
            :class="{ active: isActive('bundles-with-product') }"
            href="#bundles-with-product"
            @click.prevent="scroll('#bundles-with-product')"
          >
            {{ translate('fe_app_pdp_attribute_product_in_bundles') }}
          </a>
          <a
            v-if="hasExpertReviews"
            class="smooth-scroll link-gray"
            :class="{ active: isActive('expert-reviews') }"
            href="#expert-reviews"
            @click.prevent="scroll('#expert-reviews')"
          >
            {{ translate('pdp_subnav_awards') }}
          </a>
          <a
            v-if="product.product_id"
            class="smooth-scroll link-gray"
            :class="{ active: isActive('characteristics') }"
            href="#characteristics"
            @click.prevent="scroll('#characteristics')"
          >
            {{ translate('pdp_subnav_fact_sheet') }}
          </a>
          <a
            v-if="hasNutritionalData"
            class="smooth-scroll link-gray"
            :class="{ active: isActive('nutritional-data') }"
            href="#nutritional-data"
            @click.prevent="scroll('#nutritional-data')"
          >
            {{ translate('pdp_subnav_nutritional_data') }}
          </a>
          <a
            v-if="hasCustomerReviews"
            class="smooth-scroll link-gray mx-3 pb-2 text-uppercase"
            :class="{ active: isActive('customer-reviews') }"
            href="#customer-reviews"
            @click.prevent="scroll('#customer-reviews')"
          >
            {{ translate('pdp_subnav_sutomer_reviews') }}
          </a>

          <a
            v-if="calculated.isBundle"
            class="smooth-scroll link-gray"
            :class="{ active: isActive('bundle-items') }"
            href="#bundle-items"
            @click.prevent="scroll('#bundle-items')"
          >
            {{ translate('fe_app_pdp_attribute_bundle_items') }}
          </a>
          <a
            v-if="showManufacturer"
            class="smooth-scroll link-gray"
            :class="{ active: isActive('winemaker') }"
            href="#winemaker"
            @click.prevent="scroll('#winemaker')"
          >
            {{ translate('pdp_subnav_cutomer_more_from_the_winemaker') }}
          </a>
        </div>
      </VSheet>
      <div
        v-if="product.product_id"
        class="v-row mx-0 font-size-regular pb-lg-5"
      >
        <div class="v-col-12 v-col-lg-8 text-left pa-0">
          <VExpansionPanels v-model="panels" variant="accordion" multiple eager>
            <VExpansionPanel
              v-if="hasDescriptions"
              value="intro-description"
              id="intro-description"
            >
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('pdp_subnav_about_the_product') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <ProductDescription class="nav-section" />
              </VExpansionPanelText>
            </VExpansionPanel>

            <VExpansionPanel
              v-if="isInBundle && !calculated.isBundle"
              value="bundles-with-product"
              id="bundles-with-product"
            >
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('fe_app_pdp_attribute_product_in_bundles') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <ProductInBundles
                  class="nav-section"
                  :bundle-items="bundleIds"
                />
              </VExpansionPanelText>
            </VExpansionPanel>
            <VExpansionPanel
              v-if="hasExpertReviews"
              value="expert-reviews"
              id="expert-reviews"
            >
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('pdp_subnav_awards') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <ExpertReviews class="nav-section" />
              </VExpansionPanelText>
            </VExpansionPanel>
            <VExpansionPanel value="characteristics" id="characteristics">
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('pdp_subnav_fact_sheet') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <Characteristics class="nav-section" />
              </VExpansionPanelText>
            </VExpansionPanel>
            <VExpansionPanel
              v-if="hasSafetyNote"
              value="false"
              id="safety-note"
            >
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('pdp_subnav_safety_note') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <span class=""> {{ calculated.safetyNote }} </span>
              </VExpansionPanelText>
            </VExpansionPanel>
            <VExpansionPanel
              v-if="hasNutritionalData"
              value="nutritional-data"
              id="nutritional-data"
            >
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('pdp_subnav_nutritional_data') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <NutritionalData class="nav-section" />
              </VExpansionPanelText>
            </VExpansionPanel>
            <VExpansionPanel
              v-if="hasCustomerReviews"
              value="customer-reviews"
              id="customer-reviews"
            >
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('pdp_subnav_sutomer_reviews') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <CustomerReviewsWrapper class="nav-section" />
              </VExpansionPanelText>
            </VExpansionPanel>

            <VExpansionPanel
              v-if="calculated.isBundle"
              value="bundle-items"
              id="bundle-items"
            >
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('fe_app_pdp_attribute_bundle_items') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <BundleItems class="nav-section" />
              </VExpansionPanelText>
            </VExpansionPanel>
            <VExpansionPanel
              v-if="showManufacturer"
              value="winemaker"
              id="winemaker"
            >
              <VExpansionPanelTitle>
                <h2 class="heading">
                  {{ translate('pdp_subnav_cutomer_more_from_the_winemaker') }}
                </h2>
              </VExpansionPanelTitle>
              <VExpansionPanelText class="px-5 py-3">
                <WineMaker
                  :key="'winemaker-' + product.product_id"
                  class="nav-section"
                />
              </VExpansionPanelText>
            </VExpansionPanel>
          </VExpansionPanels>
        </div>
        <div class="d-none d-lg-inline-block v-col-lg-4 text-center">
          <div
            id="summary-cta"
            class="position-sticky text-center"
            :style="catSummaryOffset"
          >
            <ProductImage
              :key="`pi-${product.product_id}`"
              :height="220"
              :width="220"
              :product-id="product.product_id"
              :campaign-id="product.campaign_id"
              :alt="product.name"
              :is-bundle="calculated.isBundle"
            />
            <Price
              :loading="!product.product_id"
              :price="calculated.priceObject"
              class="py-2 d-flex flex-column align-center"
            />
            <MilesAndMoreBanner
              :earn="milesAndMoreData.earn"
              :spend="milesAndMoreData.spend"
            />
            <Cta
              class="pt-1"
              :no-text="true"
              :product="product"
              :pre-selected-qty="calculated.preSelectedQty"
              data-elbcontext="position:details"
            />
          </div>
        </div>
      </div>
    </VSheet>
    <VSheet
      v-if="
        product.product_id &&
        (product.back_to_referrer_link || calculated.backToReferrerForced)
      "
      class="my-2 w-100"
    >
      <VBtnSecondary
        v-bind="
          calculated.backToReferrerForced
            ? { to: calculated.backToReferrer }
            : { href: calculated.backToReferrer }
        "
        class="my-4"
      >
        {{
          calculated.backToReferrerTitle ||
          product.back_to_referrer_title ||
          translate('fe_app_pdp_back_to_offers')
        }}
      </VBtnSecondary>
    </VSheet>
  </VContainer>
</template>

<script>
import ProductDescription from '@/components/pdp/molecules/ProductDescription.vue'
import { PRODUCT_TYPES } from '@/constants/GlobalConstants'
import { usePdpStore } from '@/stores/pdp'
import { mapActions, mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'
import { useGoTo } from 'vuetify'

const Characteristics = defineAsyncComponent(
  () => import('@/components/pdp/molecules/Characteristics.vue')
)
const NutritionalData = defineAsyncComponent(
  () => import('@/components/pdp/molecules/NutritionalData.vue')
)
const Cta = defineAsyncComponent(
  () => import('@/components/basic/molecules/Cta.vue')
)
const LoadingOverlay = defineAsyncComponent(
  () => import('@/components/basic/atoms/LoadingOverlay.vue')
)
const ExpertReviews = defineAsyncComponent(
  () => import('@/components/pdp/organisms/ExpertReviews.vue')
)
const MilesAndMoreBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/MilesAndMoreBanner.vue')
)
const Price = defineAsyncComponent(
  () => import('@/components/basic/molecules/Price.vue')
)
const WineMaker = defineAsyncComponent(
  () => import('@/components/pdp/molecules/WineMaker.vue')
)
const BundleItems = defineAsyncComponent(
  () => import('@/components/pdp/molecules/BundleItems.vue')
)
const ProductImage = defineAsyncComponent(
  () => import('@/components/basic/atoms/ProductImage.vue')
)
const CustomerReviewsWrapper = defineAsyncComponent(
  () => import('@/components/pdp/organisms/CustomerReviewsWrapper.vue')
)
const ProductInBundles = defineAsyncComponent(
  () => import('@/components/pdp/molecules/ProductInBundles.vue')
)

const ALL_CHARACTERISTIC_PANELS = [
  'intro-description',
  'bundles-with-product',
  'bundle-items',
  'expert-reviews',
  'characteristics',
  'safety-note',
  'nutritional-data',
  'customer-reviews',
  'winemaker'
]

const NEW_THEME_OFFSET = '232px'
export const PDP_NAV_REBRANDING_OFFSET = '220'
const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: UtilityHelper, throttle },
      { default: DomHelper },
      { HTML_EVENT },
      { useLazyStore }
    ] = await Promise.all([
      await import('@/services/helpers/UtilityHelper'),
      await import('@/services/helpers/DomHelper'),
      await import('@/constants/GlobalConstants'),
      await import('@/stores/lazyStore')
    ])
    const lazyStore = useLazyStore()
    cache = {
      lazyStore,
      UtilityHelper,
      throttle,
      DomHelper,
      HTML_EVENT
    }

    return cache
  }
})()

const _handleScroll = async (navbar, clb) => {
  const { DomHelper } = await _getModules()
  const navbarHeight = navbar?.offsetHeight ?? 0

  Array.from(document.getElementsByClassName('nav-section')).every(
    (section) => {
      if (DomHelper.elementInViewport(section, navbarHeight)) {
        clb(section.id)
        return false
      }
      return true
    }
  )
}

export default {
  name: 'Details',
  components: {
    Price,
    Cta,
    MilesAndMoreBanner,
    LoadingOverlay,
    ProductDescription,
    Characteristics,
    NutritionalData,
    ExpertReviews,
    ProductImage,
    CustomerReviewsWrapper,
    WineMaker,
    BundleItems,
    ProductInBundles
  },
  data() {
    return {
      activeNav: '',
      panels: ALL_CHARACTERISTIC_PANELS
    }
  },
  computed: {
    ...mapState(usePdpStore, {
      product: 'getApiProduct',
      bundleIds: 'getBundleProductIds',
      metaProduct: 'getMetaProduct',
      calculated: 'getCalculated'
    }),
    isInBundle() {
      return Array.isArray(this.bundleIds) && this.bundleIds.length > 0
    },

    milesAndMoreData() {
      return this.metaProduct.milesandmore
    },
    hasDescriptions() {
      return this.product.description || this.product.catch_phrase
    },
    hasExpertReviews() {
      // v2 sends null where v1 sent an empty name
      return !!this.product.expert_reviews?.[0]?.name
    },
    hasNutritionalData() {
      return (
        this.calculated.nutritionalData &&
        this.calculated.nutritionalData.ingredients !== '' &&
        this.product.show_nutritional_note &&
        (this.product.is_single_item_bundle ||
          this.product.type === PRODUCT_TYPES.SIMPLE)
      )
    },
    hasSafetyNote() {
      return this.calculated.safetyNote && this.calculated.safetyNote !== ''
    },
    hasCustomerReviews() {
      return this.calculated.isSimple && this.product.customer_rating
    },
    isActive() {
      return (nav) => nav === this.activeNav
    },
    showManufacturer() {
      return (
        this.product.manufacturer_enabled &&
        this.product.manufacturer_link &&
        this.product.manufacturer_related_products.length > 0
      )
    },
    navHeight() {
      return this.$refs['navbar']?.offsetHeight ?? 0
    },
    catSummaryOffset() {
      return {
        top: NEW_THEME_OFFSET
      }
    }
  },
  setup() {
    const goTo = useGoTo()
    return { goTo }
  },
  methods: {
    ...mapActions(usePdpStore, ['fetchBundleIdsForProduct']),
    scroll(id) {
      const panelId = id.replace('#', '')
      if (!this.panels.includes(panelId)) {
        this.panels.push(panelId)
      }
      this.goTo(id, {
        offset: -this.navHeight - PDP_NAV_REBRANDING_OFFSET
      })
    },
    async setActiveNav(activeNav) {
      this.activeNav = activeNav
    }
  },
  async mounted() {
    const { throttle, HTML_EVENT } = await _getModules()
    await this.fetchBundleIdsForProduct()

    const handler = throttle(() => {
      _handleScroll(this.$refs['navbar'], this.setActiveNav)
    }, 100)

    document.addEventListener(HTML_EVENT.SCROLL, handler)

    this.setActiveNav('intro-description')
    if (this.$route.hash) {
      setTimeout(() => {
        this.scroll(this.$route.hash)
      }, 100)
    }

    this.$watch(
      () => this.$vuetify.display.smAndDown,
      (isSmallScreen) => {
        if (isSmallScreen) {
          this.panels = [
            ALL_CHARACTERISTIC_PANELS[0],
            ALL_CHARACTERISTIC_PANELS[1]
          ]
        } else {
          this.panels = ALL_CHARACTERISTIC_PANELS
        }
      },
      { immediate: true } // Trigger immediately on mount
    )
    this.$watch(
      () => this.$route.params,
      async () => {
        await this.fetchBundleIdsForProduct()
      }
    )
  }
}
</script>

<style scoped>
#nav-wrapper {
  padding: 25px 0;
  overflow-x: auto;
  white-space: nowrap;
  min-height: 68px;
  font-size: 11px;
}

#nav-wrapper a {
  text-transform: uppercase;
  margin: 0 16px;
  padding-bottom: 8px;
}

:deep(.manufacturer-name) {
  font-size: var(--m-font-size);
  font-family: var(--area-font-family);
  letter-spacing: 1px;
  color: rgb(var(--v-theme-clay));
  font-weight: 700;
}

:deep(.final-price) {
  font-size: var(--xxl-font-size);
}

:deep(.smooth-scroll:hover) {
  color: rgb(var(--v-theme-graphit));
}

:deep(.smooth-scroll:before) {
  background-color: rgb(var(--v-theme-graphit));
  height: 2px;
  bottom: -5px;
}

:deep(.smooth-scroll.active) {
  color: rgb(var(--v-theme-graphit));
  transform: scaleX(0);
  transition: all var(--transition-duration-l) var(--transition-timing-ease) 0s;
}

:deep(.smooth-scroll.active:before) {
  content: '';
  visibility: visible;
  transform: scaleX(1);
}

:deep(.v-container.nav-item) {
  border-bottom: 1px solid rgb(var(--v-theme-beige));
}

.display-lg-and-up :deep(.dd-container:after) {
  content: none;
}

.display-lg-and-up :deep(.v-container.item) {
  border-bottom: none;
}

h2.heading {
  font-family: var(--area-font-family);
  font-size: var(--m-font-size);
  color: rgb(var(--v-theme-gray));
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  margin-top: 0;
  margin-bottom: 0;
  outline: none;
}
</style>
